import React, { useCallback, useEffect, useState } from 'react';
import { SourceDetailForm } from '../source-detail-form';
import { SourceDetailViewer } from '../source-detail-view';
import { ConductorCommissioningPanel } from '../conductor-commissioning-panel';
import { useNavigate, useParams, useLocation } from 'react-router-dom';
import {
  Bng,
  Hmm,
  Core,
  useSourceDetailEditQuery,
  useRemoveCoreMutation,
  useRemoveHmmMutation,
  useRemoveBngMutation,
  useRemoveConductorMutation,
  useInitializeSourceMutation,
  useRepublishConductorMetadataMutation,
  SourceDetailEditQuery,
  Source,
} from '../../../../types/generated-types';
import {
  Typography,
  FormGroup,
  FormControlLabel,
  Checkbox,
} from '@mui/material';
import { Notifier } from '../../../system/services/notificationManager';
import {
  BottomMenuItem,
  useInjectableComponents,
} from '../../../system/services/injectableComponentsManager';
import { Remove, TaskAlt, Visibility, Edit, Sync } from '@mui/icons-material';
import FullWidthLoadingSkeleton from '../../shared/fullWidthLoadingSkeleton';
import {
  ConductorInitializationOptions,
  ConductorTransportMode,
  canRepublishConductorMetadata,
} from './conductor-initialization-options';

type MySource = SourceDetailEditQuery['sourceById'];
const isConductorSource = (source?: MySource) =>
  ((source?.type ?? '') as string) === 'Conductor';

export const SourceDetailHandler = () => {
  const { id } = useParams<{ id: string }>();
  const [source, setSource] = useState<MySource>();
  const { data, error, loading } = useSourceDetailEditQuery({
    variables: { id: id ?? '' },
  });

  useEffect(() => {
    if (error) {
      Notifier.error(
        'There was a problem fetching the source information: ',
        error,
      );
    }
  }, [error]);

  useEffect(() => {
    if (data) {
      setSource(data.sourceById);
    }
  }, [data]);

  const navigate = useNavigate();
  const { pathname } = useLocation();

  const [initializeSource] = useInitializeSourceMutation();
  const [republishConductorMetadata] = useRepublishConductorMetadataMutation();
  const [deleteCore] = useRemoveCoreMutation();
  const [deleteHmm] = useRemoveHmmMutation();
  const [deleteBng] = useRemoveBngMutation();
  const [deleteConductor] = useRemoveConductorMutation();

  const [pushMeta, setPushMeta] = useState(false);
  const [pushHistoricReadings, setPushHistoricReadings] = useState(false);
  const [conductorTransportMode, setConductorTransportMode] =
    useState<ConductorTransportMode>(ConductorTransportMode.Auto);
  const [useInstalledCredentials, setUseInstalledCredentials] = useState(true);
  const [conductorCredentialRef, setConductorCredentialRef] = useState('');
  const [viewMode, setViewMode] = useState(false);

  useEffect(() => {
    setViewMode(pathname?.endsWith('view-source'));
  }, [pathname]);

  const handleInitializeSource = useCallback(async () => {
    const credentialRef = conductorCredentialRef.trim();
    if (
      isConductorSource(source) &&
      !useInstalledCredentials &&
      credentialRef.length === 0
    ) {
      Notifier.error('Enter an installed Conductor credential profile');
      return;
    }

    try {
      await initializeSource({
        variables: {
          id: id ?? '',
          options: isConductorSource(source)
            ? {
                pushMeta: true,
                pushHistoricReadings,
                autoDiscovery: true,
                phase1Simulators: false,
                conductorTransportMode,
                conductorWebCredentialRef: useInstalledCredentials
                  ? undefined
                  : credentialRef,
              }
            : { pushMeta, pushHistoricReadings },
        },
      });

      Notifier.success('Source initialized');
    } catch (e) {
      Notifier.error('Error initializing Source');
    } finally {
      setPushMeta(false);
      setPushHistoricReadings(false);
      setConductorTransportMode(ConductorTransportMode.Auto);
      setUseInstalledCredentials(true);
      setConductorCredentialRef('');
    }
  }, [
    conductorCredentialRef,
    conductorTransportMode,
    id,
    initializeSource,
    pushHistoricReadings,
    pushMeta,
    source,
    useInstalledCredentials,
  ]);

  const handleRepublishConductorMetadata = useCallback(async () => {
    try {
      await republishConductorMetadata({ variables: { id: id ?? '' } });
      Notifier.success('Conductor device metadata republished');
    } catch (e) {
      Notifier.error('Unable to republish Conductor device metadata');
    }
  }, [id, republishConductorMetadata]);

  const handleRemoveSource = useCallback(async () => {
    if (id && source) {
      const type = source.type;
      switch (type) {
        case 'Core':
          try {
            await deleteCore({
              variables: { id },
              refetchQueries: ['SourceList'],
            });
            Notifier.success('Core deleted');
            navigate('/sources');
          } catch (e) {
            Notifier.error('Unable to delete Core');
          }
          break;
        case 'BNG':
          try {
            await deleteBng({
              variables: { id },
              refetchQueries: ['SourceList'],
            });
            Notifier.success('BNG deleted');
            navigate('/sources');
          } catch (e) {
            Notifier.error('Unable to delete BNG');
          }
          break;
        case 'HMM':
          try {
            await deleteHmm({
              variables: { id },
              refetchQueries: ['SourceList'],
            });
            navigate('/sources');
          } catch (e) {
            Notifier.error('Unable to delete HMM');
          }
          break;
        case 'Conductor':
          try {
            await deleteConductor({
              variables: { id },
              refetchQueries: ['SourceList'],
            });
            Notifier.success('Conductor deleted');
            navigate('/sources');
          } catch (e) {
            Notifier.error('Unable to delete Conductor');
          }
          break;
        default:
      }
    } else {
      Notifier.error('No source found: Check URL parameter');
    }
  }, [deleteBng, deleteConductor, deleteCore, deleteHmm, id, navigate, source]);

  const { setContextMenuItems } = useInjectableComponents();

  useEffect(() => {
    if (source && !error) {
      const isConductor = isConductorSource(source);
      const initAction = {
        actionLabel: 'Initialize',
        cancelLabel: 'Cancel',
        title: `Initialize ${source.type ?? 'Source'}`,
        prompt: isConductor
          ? 'Initializing this Conductor will configure its reporting destination and discover available devices. Device metadata is collected automatically for mapping and provisioning. Readings will not be published until you review and commit the mappings.'
          : 'Initializing this Source will set/update the push destination and configuration, and send all applicable Zone Policies. Are you sure?',
        customContent: (
          <>
            <h4>{isConductor ? 'Discovery options' : 'Advanced options'}</h4>
            {isConductor ? (
              <ConductorInitializationOptions
                transportMode={conductorTransportMode}
                onTransportModeChange={setConductorTransportMode}
                useInstalledCredentials={useInstalledCredentials}
                onUseInstalledCredentialsChange={setUseInstalledCredentials}
                credentialRef={conductorCredentialRef}
                onCredentialRefChange={setConductorCredentialRef}
              />
            ) : (
              <FormGroup>
                <FormControlLabel
                  control={
                    <Checkbox
                      color="secondary"
                      value={pushMeta}
                      onChange={(_, checked) => {
                        setPushMeta(checked);
                      }}
                    />
                  }
                  label="Request Source to push Meta for all devices"
                />
              </FormGroup>
            )}
            {source.type === 'Core' && (
              <FormGroup>
                <FormControlLabel
                  control={
                    <Checkbox
                      color="secondary"
                      value={pushHistoricReadings}
                      onChange={(_, checked) => {
                        setPushHistoricReadings(checked);
                      }}
                    />
                  }
                  label="Request Source to push Historic Readings for all devices"
                />
              </FormGroup>
            )}
          </>
        ),
        action: handleInitializeSource,
      };
      const removeAction = {
        actionLabel: 'Remove',
        cancelLabel: 'Cancel',
        title: `Remove ${source.type ?? 'Source'}`,
        prompt: 'Are you sure? This action cannot be reversed',
        action: handleRemoveSource,
      };

      const items: BottomMenuItem[] = [];
      if (source.type === 'Core' || source.type === 'HMM' || isConductor) {
        // Only Cores, HMMs, and Conductors can be initialized.
        items.push({
          id: 'initialize-source-menu-item',
          label: 'Initialize Source',
          icon: <TaskAlt fontSize="small" />,
          confirmableAction: initAction,
          permit: {
            action: 'update',
            subject: source as Source,
          },
        });
      }

      if (
        canRepublishConductorMetadata(
          source.type,
          'lastDiscoveryStatus' in source
            ? source.lastDiscoveryStatus
            : undefined,
        )
      ) {
        items.push({
          id: 'republish-conductor-metadata-menu-item',
          label: 'Republish Device Metadata',
          icon: <Sync fontSize="small" />,
          confirmableAction: {
            actionLabel: 'Republish',
            cancelLabel: 'Cancel',
            title: 'Republish Device Metadata',
            prompt:
              'Request current device metadata from this commissioned Conductor and send it to Super Push?',
            action: handleRepublishConductorMetadata,
          },
          permit: {
            action: 'update',
            subject: source as unknown as Source,
          },
        });
      }

      items.push({
        id: 'remove-source-menu-item',
        label: 'Remove Source',
        icon: <Remove fontSize="small" />,
        confirmableAction: removeAction,
        permit: {
          action: 'delete',
          subject: source as Source,
        },
      });

      if (!pathname.includes('edit-source')) {
        items.push({
          id: 'edit-source-menu-item',
          label: 'Edit Source Details',
          icon: <Edit fontSize="small" />,
          navTarget: `/sources/${id}/edit-source`,
        });
      }

      if (!pathname.includes('view-source')) {
        items.push({
          id: 'view-source-menu-item',
          label: 'View Source Details',
          icon: <Visibility fontSize="small" />,
          navTarget: `/sources/${id}/view-source`,
        });
      }

      setContextMenuItems(items.length > 0 ? [{ items }] : undefined);
    } else {
      setContextMenuItems(undefined);
    }
    return () => {
      setContextMenuItems(undefined);
    };
  }, [
    source,
    error,
    handleInitializeSource,
    handleRemoveSource,
    handleRepublishConductorMetadata,
    conductorCredentialRef,
    conductorTransportMode,
    pushMeta,
    pushHistoricReadings,
    useInstalledCredentials,
    pathname,
    id,
    setContextMenuItems,
  ]);

  return error ? (
    <div>Unable to access requested source.</div>
  ) : loading ? (
    <FullWidthLoadingSkeleton />
  ) : (
    <div
      style={{
        display: 'grid',
        gridTemplateRows: 'auto 1fr',
        height: '100%',
        overflowY: 'scroll',
      }}
    >
      <Typography variant="h3" sx={{ fontSize: '1.4rem', margin: '1rem' }}>
        {data?.sourceById ? data?.sourceById?.name : 'Create Source'}
      </Typography>
      {viewMode && data?.sourceById ? (
        <>
          <SourceDetailViewer source={source as Partial<Core | Bng | Hmm>} />
          {isConductorSource(source) ? (
            <ConductorCommissioningPanel
              buildingId={
                source && 'buildingId' in source ? source.buildingId : undefined
              }
              propertyId={source?.propertyId}
              sourceId={source?._id ?? ''}
            />
          ) : null}
        </>
      ) : data?.sourceById ? (
        <>
          <SourceDetailForm source={source as Partial<Core | Bng | Hmm>} />
          {isConductorSource(source) ? (
            <ConductorCommissioningPanel
              buildingId={
                source && 'buildingId' in source ? source.buildingId : undefined
              }
              propertyId={source?.propertyId}
              sourceId={source?._id ?? ''}
            />
          ) : null}
        </>
      ) : (
        <SourceDetailForm />
      )}
    </div>
  );
};
